import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { useGlobalConnect } from "@/hooks/useGlobalConnect";
import { SceneType } from "@/hooks/useScenes";
import SceneArenaView from "@/components/global-connect/SceneArenaView";
import { useGeoData } from "@/hooks/useGeoData";
import { toast } from "sonner";

const GlobalConnectScene = () => {
  const { countryCode, cityId, scene } = useParams<{
    countryCode: string;
    cityId: string;
    scene: string;
  }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { settings, useAction } = useGlobalConnect();
  const { getCountryFlag } = useGeoData();

  const [country, setCountry] = useState<{ name: string; flag: string } | null>(null);
  const [city, setCity] = useState<{ name: string } | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch country and city info
  useEffect(() => {
    const init = async () => {
      if (!countryCode || !cityId) return;

      // Fetch country info (prefer geo cache; fallback to legacy table)
      const upper = countryCode.toUpperCase();
      const flag = getCountryFlag(upper);

      const geoCountryRes = await supabase
        .from("countries")
        .select("name")
        .eq("code", upper)
        .maybeSingle();

      if (geoCountryRes.data?.name) {
        setCountry({ name: geoCountryRes.data.name, flag });
      } else {
        const countryRes = await supabase
          .from("countries")
          .select("name, flag")
          .eq("code", upper)
          .maybeSingle();

        if (countryRes.data?.name) {
          setCountry({ name: countryRes.data.name, flag: countryRes.data.flag || flag });
        } else {
          setCountry({ name: upper, flag });
        }
      }

      // Handle virtual cities (for countries without cities in DB)
      if (cityId.startsWith("virtual-")) {
        // Use country name as city name for virtual cities
        setCity({ name: (geoCountryRes.data as any)?.name || upper });
      } else {
        const cityRes = await supabase
          .from("cities")
          .select("name")
          .eq("id", cityId)
          .single();

        if (cityRes.data) setCity(cityRes.data);
      }

      setLoading(false);
    };

    init();
  }, [countryCode, cityId]);

  // Handle popping a user from the scene
  const handlePopUser = useCallback(
    async (userId: string) => {
      if (!user || !countryCode) return;

      try {
        const canProceed = await useAction("pop");
        if (!canProceed) return;

        // Check for mutual pop
        const { data: mutualPop } = await supabase
          .from("pops")
          .select("id")
          .eq("popper_id", userId)
          .eq("popped_id", user.id)
          .maybeSingle();

        const { error: insertError } = await supabase.from("pops").insert({
          popper_id: user.id,
          popped_id: userId,
          tab_context: "global_connect_scene",
          global_connect_country: countryCode.toUpperCase(),
          global_connect_intent: settings?.intent_level,
          source_context: {
            country_code: countryCode.toUpperCase(),
            city_id: cityId,
            city_name: city?.name,
            scene: scene,
          },
        });

        if (insertError) {
          console.error("Error inserting pop:", insertError);
          toast.error("Failed to pop. Try again.");
          return;
        }

        if (mutualPop) {
          // It's a match!
          const { error: matchError } = await supabase.from("matches").insert({
            user1_id: user.id < userId ? user.id : userId,
            user2_id: user.id < userId ? userId : user.id,
            met_context: "global_connect_scene",
            global_connect_country: countryCode.toUpperCase(),
            is_global_match: true,
            match_source: "global_connect",
            source_context: { 
              country_code: countryCode.toUpperCase(), 
              city_id: cityId, 
              scene,
              city_name: city?.name,
            },
          });

          if (!matchError) {
            await useAction("match");
            toast.success("🎉 It's a match!");
            
            // Trigger Spark Chain Reaction for matches
            try {
              const state = (await supabase.channel(`scene:${countryCode}:${cityId}:${scene}`).presenceState()) as any;
              const compatibleUsers: string[] = [];
              
              Object.values(state).forEach((presences: unknown[]) => {
                (presences as Array<{ id: string; intents?: string[] }>).forEach((p) => {
                  if (
                    p.id !== user.id &&
                    p.id !== userId &&
                    compatibleUsers.length < 5
                  ) {
                    compatibleUsers.push(p.id);
                  }
                });
              });

              for (const uid of compatibleUsers.slice(0, 3)) {
                await supabase.from("notifications").insert({
                  user_id: uid,
                  type: "spark_chain",
                  title: "✨ Spark Chain Reaction",
                  body: `A match happened nearby in ${city?.name || countryCode}—want to join the vibe?`,
                  data: {
                    country_code: countryCode.toUpperCase(),
                    city_id: cityId,
                    scene: scene,
                    interaction_type: "match",
                  },
                });
              }
            } catch (err) {
              console.error("Error triggering spark chain:", err);
            }
          }
        } else {
          toast.success("Popped! 🎈");
        }
      } catch (error) {
        console.error("Error in handlePopUser:", error);
        toast.error("Something went wrong. Try again.");
      }
    },
    [user, countryCode, settings?.intent_level, useAction]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!country || !city || !scene || !countryCode || !cityId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Scene not found</p>
      </div>
    );
  }

  return (
    <SceneArenaView
      countryCode={countryCode.toUpperCase()}
      countryName={country.name}
      countryFlag={country.flag}
      cityId={cityId}
      cityName={city.name}
      scene={scene as SceneType}
      onBack={() => navigate("/global-connect")}
      onPopUser={handlePopUser}
    />
  );
};

export default GlobalConnectScene;
